import type { PriceChangeRequest, PriceSuggestionItem, PriceSuggestionList } from "@station/contracts";
import { newIdempotencyKey } from "@station/api-client";
import { addDays, type LocalDate, toIstanbulDate } from "@station/core";
import {
  Alert,
  Button,
  Card,
  Checkbox,
  DateInput,
  EmptyState,
  FormField,
  Input,
  SkeletonRows,
  StatusBadge,
  useToast,
} from "@station/ui";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { BadgePercent, Send, X } from "lucide-react";
import { type ReactElement, useState } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { Th } from "../../../features/fuel/shared.js";
import { useGoodsReceipt, usePriceSuggestions } from "../../../features/market/purchasing-queries.js";
import {
  formatPercent,
  priceApplyBody,
  priceChangeDirection,
  suggestedPriceText,
} from "../../../features/market/pricing-values.js";
import { useMarketPermissions, useRefreshMarket } from "../../../features/market/queries.js";
import {
  MarketPage,
  marketSearchShape,
  MarketStationField,
  NoStations,
  TableFrame,
  useMarketStations,
} from "../../../features/market/shared.js";
import { trTextToApiDecimal } from "../../../features/pos/pos-values.js";
import { compactSearch } from "../../../features/shifts/shift-values.js";
import { api } from "../../../lib/api.js";
import { errorMessage } from "../../../lib/errors.js";
import { queryKeys } from "../../../lib/query.js";
import { formatUnitPrice } from "../../../lib/format.js";

const searchSchema = z.object({
  ...marketSearchShape,
  receipt: z.string().uuid().optional().catch(undefined),
  below: z.enum(["true", "false"]).optional().catch(undefined),
});

export const Route = createFileRoute("/_app/market/fiyat-onerisi")({
  validateSearch: (search) => searchSchema.parse(search),
  component: PriceSuggestionsRoute,
});

/**
 * Satış fiyatı önerisi (Prompt 16 — web, aşama B; ADR 0016 §16). Öneri = ortalama maliyet × (1 + KDV) / (1 − hedef
 * marj), ayardaki adıma yukarı yuvarlanır; hedef marj kategoriden, yoksa ayardan. Seçilen öneriler (fiyat
 * düzenlenebilir) fiyat modülünde TASLAK fiyat talebine dönüşür — onay ve yayın mevcut akıştadır (`/fiyatlar`).
 * Okuma `market.read`; talep `fuel.price.change`. Mal kabulden `?receipt=` ile yalnızca o kabulün ürünleri gelir.
 */
function PriceSuggestionsRoute(): ReactElement {
  const { t } = useTranslation();
  return (
    <MarketPage
      title={t("menu.marketPriceSuggestions")}
      description={t("market.prices.description")}
      breadcrumb={[{ label: t("menu.marketPriceSuggestions") }]}
    >
      {({ companyId }) => <PriceSuggestionsBody companyId={companyId} />}
    </MarketPage>
  );
}

function PriceSuggestionsBody({ companyId }: { companyId: string }): ReactElement {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const search = Route.useSearch();
  const station = useMarketStations(companyId, { required: true });
  // Varsayılan: yalnızca hedefin altındakiler (mal kabulden gelince tüm kabul ürünleri gösterilir).
  const belowTargetOnly = search.below === undefined ? search.receipt === undefined : search.below === "true";
  const suggestions = usePriceSuggestions(station.stationId, { receiptId: search.receipt, belowTargetOnly });

  const setSearch = (patch: Record<string, unknown>) =>
    void navigate({
      to: ".",
      search: ((previous: Record<string, unknown>) => compactSearch({ ...previous, ...patch })) as never,
      replace: true,
    });

  if (station.error) return <Alert tone="danger">{errorMessage(station.error)}</Alert>;
  if (station.isLoading) return <SkeletonRows rows={4} />;
  if (station.stationId === null) return <NoStations />;

  return (
    <>
      <Card className="flex flex-wrap items-end gap-3 p-4">
        <MarketStationField selection={station} allowAll={false} />
        <label className="flex min-h-10 items-center gap-2 text-sm">
          <Checkbox
            checked={belowTargetOnly}
            onChange={(event) => setSearch({ below: event.target.checked ? "true" : "false" })}
          />
          {t("market.prices.belowTargetOnly")}
        </label>
        {search.receipt ? (
          <ReceiptFilter receiptId={search.receipt} onClear={() => setSearch({ receipt: undefined })} />
        ) : null}
      </Card>

      {suggestions.error ? (
        <Alert tone="danger">{errorMessage(suggestions.error)}</Alert>
      ) : !suggestions.data ? (
        <SkeletonRows rows={6} />
      ) : (
        <SuggestionTable
          key={`${station.stationId}:${search.receipt ?? ""}:${belowTargetOnly}`}
          list={suggestions.data}
          stationId={station.stationId}
        />
      )}
    </>
  );
}

/** Mal kabul süzgeci: kabulün numarası gösterilir ve tek dokunuşla kaldırılır. */
function ReceiptFilter({ receiptId, onClear }: { receiptId: string; onClear: () => void }): ReactElement {
  const { t } = useTranslation();
  const receipt = useGoodsReceipt(receiptId);
  return (
    <span className="flex items-center gap-2 rounded-md border px-3 py-1.5 text-sm">
      {t("market.prices.receiptFilter", { no: receipt.data?.receiptNo ?? "…" })}
      <Button variant="ghost" size="icon" aria-label={t("market.prices.clearReceipt")} onClick={onClear}>
        <X aria-hidden="true" />
      </Button>
    </span>
  );
}

function SuggestionTable({
  list,
  stationId,
}: {
  list: PriceSuggestionList;
  stationId: string;
}): ReactElement {
  const { t } = useTranslation();
  const toast = useToast();
  const queryClient = useQueryClient();
  const refresh = useRefreshMarket();
  const permissions = useMarketPermissions();
  const canApply = permissions.canChangePrices;
  const today = toIstanbulDate(new Date());
  const [selected, setSelected] = useState<Set<string>>(
    () => new Set(list.items.filter((item) => item.belowTarget).map((item) => item.productId)),
  );
  const [prices, setPrices] = useState<Record<string, string>>({});
  const [effectiveDate, setEffectiveDate] = useState<LocalDate>(addDays(today, 1));
  const [effectiveTime, setEffectiveTime] = useState("00:00");
  const [note, setNote] = useState("");
  const [showProblems, setShowProblems] = useState(false);
  const [created, setCreated] = useState<PriceChangeRequest | null>(null);
  const { body, problems } = priceApplyBody({
    stationId,
    items: list.items,
    selected,
    prices,
    effectiveDate,
    effectiveTime,
    note,
  });

  const apply = useMutation({
    mutationFn: async () =>
      (
        await api.POST("/market/price-suggestions/apply", {
          params: { header: { "Idempotency-Key": newIdempotencyKey() } },
          body,
        })
      ).data!,
    onSuccess: async (request) => {
      setCreated(request);
      setShowProblems(false);
      await Promise.all([
        refresh(),
        queryClient.invalidateQueries({ queryKey: queryKeys.priceChangeRequests() }),
      ]);
      toast({ tone: "success", title: t("market.prices.created", { no: request.requestNo }) });
    },
    onError: (caught) => toast({ tone: "danger", title: errorMessage(caught) }),
  });

  const toggle = (productId: string, checked: boolean) =>
    setSelected((current) => {
      const next = new Set(current);
      if (checked) next.add(productId);
      else next.delete(productId);
      return next;
    });

  return (
    <>
      <p className="text-sm text-muted-foreground">
        {t("market.prices.basis", { roundTo: formatUnitPrice(list.roundTo) })}
      </p>

      {list.items.length === 0 ? (
        <EmptyState
          headingLevel={2}
          icon={<BadgePercent />}
          title={t("market.prices.emptyTitle")}
          description={t("market.prices.emptyDescription")}
        />
      ) : (
        <TableFrame label={t("market.prices.caption")}>
          <thead className="bg-muted/60 text-xs uppercase text-muted-foreground">
            <tr>
              <Th>
                <span className="sr-only">{t("market.suggestions.select")}</span>
              </Th>
              <Th>{t("market.fields.product")}</Th>
              <Th align="right">{t("market.prices.fields.unitCost")}</Th>
              <Th align="right">{t("market.prices.fields.vat")}</Th>
              <Th align="right">{t("market.prices.fields.currentPrice")}</Th>
              <Th align="right">{t("market.prices.fields.currentMargin")}</Th>
              <Th align="right">{t("market.prices.fields.targetMargin")}</Th>
              <Th align="right">{t("market.prices.fields.suggestedPrice")}</Th>
              <Th align="right">{t("market.prices.fields.suggestedMargin")}</Th>
            </tr>
          </thead>
          <tbody>
            {list.items.map((item) => (
              <PriceRow
                key={item.productId}
                item={item}
                canApply={canApply}
                checked={selected.has(item.productId)}
                price={prices[item.productId] ?? suggestedPriceText(item)}
                invalid={showProblems && problems.has(item.productId)}
                onToggle={(checked) => toggle(item.productId, checked)}
                onPrice={(text) => setPrices((current) => ({ ...current, [item.productId]: text }))}
              />
            ))}
          </tbody>
        </TableFrame>
      )}

      {list.skipped.length > 0 ? (
        <Alert tone="info" title={t("market.prices.skippedTitle", { count: list.skipped.length })}>
          <ul className="mt-1 grid gap-1">
            {list.skipped.map((item) => (
              <li key={item.productId}>
                {item.productName}: {item.reason}
              </li>
            ))}
          </ul>
        </Alert>
      ) : null}

      {canApply && list.items.length > 0 ? (
        <Card className="grid gap-4 p-4">
          <div className="flex flex-wrap items-end gap-3">
            <FormField label={t("pricing.bulk.effectiveDate")} required className="w-48">
              {(control) => (
                <DateInput
                  {...control}
                  value={effectiveDate}
                  min={today}
                  onValueChange={(next) => next && setEffectiveDate(next)}
                />
              )}
            </FormField>
            <FormField label={t("pricing.bulk.effectiveTime")} required className="w-32">
              {(control) => (
                <Input
                  {...control}
                  type="time"
                  value={effectiveTime}
                  onChange={(event) => setEffectiveTime(event.target.value || "00:00")}
                />
              )}
            </FormField>
            <FormField label={t("market.fields.note")} className="min-w-64 flex-1">
              {(control) => (
                <Input
                  {...control}
                  maxLength={500}
                  value={note}
                  onChange={(event) => setNote(event.target.value)}
                />
              )}
            </FormField>
            <Button
              loading={apply.isPending}
              disabled={selected.size === 0}
              onClick={() => {
                setShowProblems(true);
                if (problems.size === 0 && body.items.length > 0) apply.mutate();
              }}
            >
              <Send aria-hidden="true" />
              {t("market.prices.apply", { count: selected.size })}
            </Button>
          </div>
          {showProblems && problems.size > 0 ? (
            <Alert tone="warning">{t("market.prices.fixProblems", { count: problems.size })}</Alert>
          ) : null}
          <p className="text-xs text-muted-foreground">{t("market.prices.applyHint")}</p>
        </Card>
      ) : !canApply && list.items.length > 0 ? (
        <p className="text-sm text-muted-foreground">{t("market.prices.cannotApply")}</p>
      ) : null}

      {created ? (
        <Alert tone="success" title={t("market.prices.createdTitle")}>
          <Link
            to="/fiyatlar"
            search={{ tab: "requests", request: created.id }}
            className="underline underline-offset-4"
          >
            {t("market.prices.openRequest", { no: created.requestNo })}
          </Link>
        </Alert>
      ) : null}
    </>
  );
}

function PriceRow({
  item,
  canApply,
  checked,
  price,
  invalid,
  onToggle,
  onPrice,
}: {
  item: PriceSuggestionItem;
  canApply: boolean;
  checked: boolean;
  price: string;
  invalid: boolean;
  onToggle: (checked: boolean) => void;
  onPrice: (text: string) => void;
}): ReactElement {
  const { t } = useTranslation();
  const errorId = `price-${item.productId}-error`;
  const direction = priceChangeDirection(item.currentPrice, trTextToApiDecimal(price));
  return (
    <tr className="border-t align-top" data-testid="price-suggestion-row">
      <td className="px-3 py-2">
        {canApply ? (
          <Checkbox
            checked={checked}
            aria-label={t("market.suggestions.selectProduct", { product: item.productName })}
            onChange={(event) => onToggle(event.target.checked)}
          />
        ) : null}
      </td>
      <td className="min-w-52 px-3 py-2">
        <span className="grid gap-0.5">
          <Link
            to="/market/stok/$productId"
            params={{ productId: item.productId }}
            className="font-medium underline underline-offset-4"
          >
            {item.productName}
          </Link>
          <span className="font-mono text-xs text-muted-foreground">
            {item.productCode}
            {item.categoryName ? ` · ${item.categoryName}` : ""}
          </span>
        </span>
        {item.belowTarget ? (
          <StatusBadge tone="warning" className="mt-1">
            {t("market.prices.belowTarget")}
          </StatusBadge>
        ) : null}
      </td>
      <td className="px-3 py-2 text-right tabular whitespace-nowrap">{formatUnitPrice(item.unitCost)}</td>
      <td className="px-3 py-2 text-right tabular">{formatPercent(item.vatRatePercent)}</td>
      <td className="px-3 py-2 text-right tabular whitespace-nowrap">
        {item.currentPrice === null ? (
          <span className="text-xs text-warning">{t("market.prices.noPrice")}</span>
        ) : (
          formatUnitPrice(item.currentPrice)
        )}
      </td>
      <td className="px-3 py-2 text-right tabular">{formatPercent(item.currentMarginPercent)}</td>
      <td className="px-3 py-2 text-right">
        <span className="grid justify-items-end gap-0.5">
          <span className="tabular">{formatPercent(item.targetMarginPercent)}</span>
          <span className="text-xs text-muted-foreground">
            {t(`market.prices.marginSource.${item.targetMarginSource}`)}
          </span>
        </span>
      </td>
      <td className="px-3 py-2 text-right">
        {canApply ? (
          <Input
            inputMode="decimal"
            className="ms-auto w-28 text-right tabular"
            aria-label={t("market.prices.priceFor", { product: item.productName })}
            aria-invalid={invalid}
            aria-describedby={invalid ? errorId : undefined}
            value={price}
            onChange={(event) => onPrice(event.target.value)}
          />
        ) : (
          <span className="tabular font-medium">{formatUnitPrice(item.suggestedPrice)}</span>
        )}
        {direction && direction !== "SAME" ? (
          <span className="mt-1 block text-xs text-muted-foreground">
            {t(`market.prices.direction.${direction}`)}
          </span>
        ) : null}
        {invalid ? (
          <p id={errorId} className="mt-1 text-xs text-destructive">
            {t("market.prices.priceProblem")}
          </p>
        ) : null}
      </td>
      <td className="px-3 py-2 text-right tabular">{formatPercent(item.suggestedMarginPercent)}</td>
    </tr>
  );
}
